// Pure geometry for framing a navigation target in the quotation viewer (REQ-455). No Apryse, no
// DOM — `AnnotationService.frameAnnotations` measures, these decide.

// "group": an eref + every object connected to it, anchored bottom-left (AC1-AC4, AC6).
// "single": a lone object with no eref, centered with wider free space (AC5).
export type TFrameMode = "group" | "single"

export type TFrameBox = { left: number; top: number; right: number; bottom: number }

// The part of the scroll element the user can actually see, in the scroll element's own viewport
// coordinates (0,0 = its top-left), i.e. minus the sidebars and the bottom-docked panel (AC7).
export type TVisibleRect = { left: number; top: number; width: number; height: number }

export type TFramePadding = { x: number; y: number }

// Marks the host of a panel docked over the BOTTOM of the document area (the keyboard-shortcuts
// panel), so framing can subtract what it covers.
export const BOTTOM_DOCK_ATTRIBUTE = "data-bottom-dock"

// Fitting always wins over readability: AC1 needs the whole group on screen, so the floor is low
// enough for any real elevation. The ceiling stops a small group / lone object zooming very deep.
export const FRAME_MIN_ZOOM = 0.1
export const FRAME_MAX_ZOOM = 3

const GROUP_MIN_PADDING_X_PX = 40
const GROUP_PADDING_X_RATIO = 0.05
const GROUP_PADDING_Y_PX = 32
// A lone object fills at most half the visible area, so ~25% stays free on each side (AC5 > AC3).
const SINGLE_PADDING_RATIO = 0.25

// AC9's tolerance: free space this small still counts as "framed", and a zoom this far below the fit
// still reads as framed rather than lost in a zoomed-out page.
const FRAMED_MIN_GAP_PX = 8
const FRAMED_MIN_ZOOM_RATIO = 0.8

export const unionBoxes = (boxes: TFrameBox[]): TFrameBox | null => {
	if (boxes.length === 0) return null
	return {
		left: Math.min(...boxes.map((b) => b.left)),
		top: Math.min(...boxes.map((b) => b.top)),
		right: Math.max(...boxes.map((b) => b.right)),
		bottom: Math.max(...boxes.map((b) => b.bottom)),
	}
}

export const resolveFramePadding = (mode: TFrameMode, visible: TVisibleRect): TFramePadding => {
	if (mode === "single") {
		return { x: visible.width * SINGLE_PADDING_RATIO, y: visible.height * SINGLE_PADDING_RATIO }
	}
	return { x: Math.max(GROUP_MIN_PADDING_X_PX, visible.width * GROUP_PADDING_X_RATIO), y: GROUP_PADDING_Y_PX }
}

// The zoom at which content of `contentWidth` × `contentHeight` (page units) fits the visible rect
// with `padding` free on every side, clamped to [minZoom, maxZoom].
export const resolveFitZoom = ({
	contentWidth,
	contentHeight,
	visible,
	padding,
	minZoom = FRAME_MIN_ZOOM,
	maxZoom = FRAME_MAX_ZOOM,
}: {
	contentWidth: number
	contentHeight: number
	visible: TVisibleRect
	padding: TFramePadding
	minZoom?: number
	maxZoom?: number
}): number => {
	const availableWidth = Math.max(1, visible.width - 2 * padding.x)
	const availableHeight = Math.max(1, visible.height - 2 * padding.y)
	const zoomX = contentWidth > 0 ? availableWidth / contentWidth : maxZoom
	const zoomY = contentHeight > 0 ? availableHeight / contentHeight : maxZoom
	return Math.max(minZoom, Math.min(maxZoom, Math.min(zoomX, zoomY)))
}

// Scroll offsets that place `box` (scroll-CONTENT coordinates) in the visible rect: bottom-left plus
// padding for a group (AC2), centered for a single object. Not clamped — the caller owns the range.
export const resolveFrameScroll = ({
	box,
	visible,
	mode,
	padding,
}: {
	box: TFrameBox
	visible: TVisibleRect
	mode: TFrameMode
	padding: TFramePadding
}): { left: number; top: number } => {
	if (mode === "group") {
		return {
			left: box.left - visible.left - padding.x,
			top: box.bottom - (visible.top + visible.height) + padding.y,
		}
	}
	return {
		left: (box.left + box.right) / 2 - visible.left - visible.width / 2,
		top: (box.top + box.bottom) / 2 - visible.top - visible.height / 2,
	}
}

// AC9: is `box` (scroll-VIEWPORT coordinates) already fully inside the visible rect with free space
// left and right, at a zoom not far below the fit? Then navigation leaves zoom and scroll alone.
export const isAlreadyFramed = ({
	box,
	visible,
	zoom,
	fitZoom,
}: {
	box: TFrameBox
	visible: TVisibleRect
	zoom: number
	fitZoom: number
}): boolean => {
	if (!(zoom > 0) || zoom < fitZoom * FRAMED_MIN_ZOOM_RATIO) return false
	return (
		box.left >= visible.left + FRAMED_MIN_GAP_PX &&
		box.right <= visible.left + visible.width - FRAMED_MIN_GAP_PX &&
		box.top >= visible.top &&
		box.bottom <= visible.top + visible.height
	)
}
